import React, { useRef, useState, useEffect } from 'react';
import { Box, Typography, Paper, Avatar, TextField, IconButton, CircularProgress, Menu, MenuItem, Popover, Grid, Dialog, Button } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import VideocamIcon from '@mui/icons-material/Videocam';
import CallIcon from '@mui/icons-material/Call';
import SettingsIcon from '@mui/icons-material/Settings';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import ImageIcon from '@mui/icons-material/Image';
import HeadphonesIcon from '@mui/icons-material/Headphones';
import MicIcon from '@mui/icons-material/Mic';
import DeleteIcon from '@mui/icons-material/Delete';
import InsertEmoticonIcon from '@mui/icons-material/InsertEmoticon';
import CloseIcon from '@mui/icons-material/Close';
import api from '../../api';

const COMMON_EMOJIS = [
  '\u{1F600}', '\u{1F602}', '\u{1F60A}', '\u{1F970}', 
  '\u{1F60E}', '\u{1F914}', '\u{1F62D}', '\u{1F621}', 
  '\u{1F44D}', '\u{1F64F}', '\u{1F389}', '\u{2764}', 
  '\u{1F525}', '\u{2705}', '\u{1F440}', '\u{1F64C}'
];

const ChatArena = ({ activeChat, messages, loadingMessages, messageInput, setMessageInput, onSendMessage, safeEmpId, baseUrl, onOpenSettings, onStartCall }) => {
  const [isUploading, setIsUploading] = useState(false);
  const [anchorElAttach, setAnchorElAttach] = useState(null);
  const [anchorElEmoji, setAnchorElEmoji] = useState(null);
  
  const [isDragOver, setIsDragOver] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  
  const messagesEndRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const isScrolledUp = useRef(false);

  const imageVideoRef = useRef(null);
  const audioRef = useRef(null);
  const docRef = useRef(null);
  const otherRef = useRef(null);

  const [isRecording, setIsRecording] = useState(false);
  const [recTime, setRecTime] = useState(0);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    isScrolledUp.current = (scrollHeight - scrollTop - clientHeight) > 150;
  };

  useEffect(() => {
    if (!isScrolledUp.current && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onSendMessage(messageInput, 'TEXT', null);
    }
  };

  const processFileUpload = async (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      alert("File exceeds 10MB limit. Please choose a smaller file.");
      return;
    }
    setIsUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await api.post('/chat/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' }});
      onSendMessage(res.data.filename, 'FILE', res.data.file_url);
    } catch (err) {
      console.error("Upload failed", err);
      alert("Failed to upload attachment.");
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileUpload = (event) => {
    setAnchorElAttach(null);
    processFileUpload(event.target.files[0]);
    event.target.value = ''; 
  };

  const handleDragOver = (e) => { e.preventDefault(); setIsDragOver(true); };
  const handleDragLeave = (e) => { e.preventDefault(); setIsDragOver(false); };
  const handleDrop = (e) => { e.preventDefault(); setIsDragOver(false); processFileUpload(e.dataTransfer.files[0]); };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) audioChunksRef.current.push(event.data);
      };

      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        if (audioBlob.size > 0 && mediaRecorderRef.current.sendOnStop) {
          setIsUploading(true);
          const formData = new FormData();
          formData.append('file', new File([audioBlob], 'VoiceNote.webm', { type: 'audio/webm' }));
          try {
            const res = await api.post('/chat/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' }});
            onSendMessage("Voice Note", 'FILE', res.data.file_url);
          } catch (err) { console.error("Voice Note Upload failed", err); } 
          finally { setIsUploading(false); }
        }
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorder.start();
      setIsRecording(true);
      setRecTime(0);
      timerRef.current = setInterval(() => { setRecTime(prev => prev + 1); }, 1000);
    } catch (err) {
      alert("Microphone access denied or not available. Voice messaging requires HTTPS.");
    }
  };

  const stopRecording = (cancel = false) => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.sendOnStop = !cancel; 
      mediaRecorderRef.current.stop();
      clearInterval(timerRef.current);
      setIsRecording(false);
    }
  };

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s < 10 ? '0' : ''}${s}`;
  };

  const handleCallClick = (isVideo) => {
    if (activeChat.type !== 'DIRECT') {
      alert("Calls are currently only supported for 1-on-1 Direct Messages.");
      return;
    }
    onStartCall(activeChat.target_emp_id, activeChat.name, isVideo);
  };

  const activeChatProfilePic = activeChat?.target_emp_id ? `${baseUrl}/employees/${activeChat.target_emp_id}/profile-picture?t=${Date.now()}` : (activeChat?.image_url || null);

  const renderMessageContent = (msg, isMine) => {
    if (msg.message_type === 'FILE') {
      const isImage = msg.file_url && msg.file_url.match(/\.(jpeg|jpg|gif|png|webp)$/i);
      const isVideo = msg.file_url && msg.file_url.match(/\.(mp4|webm|ogg)$/i);
      const isAudio = msg.file_url && (msg.file_url.match(/\.(mp3|wav|mpeg|ogg)$/i) || msg.text === "Voice Note");
      const downloadUrl = `${baseUrl}/chat/attachment/${msg.file_url}`;
      
      if (isImage) {
        return (
          <Box sx={{ mt: 0.5, cursor: 'pointer', display: 'inline-block' }} onClick={() => setPreviewImage(downloadUrl)}>
            <img src={downloadUrl} alt="attachment" style={{ maxWidth: '100%', maxHeight: 250, borderRadius: 8, display: 'block', border: '1px solid #e0e0e0' }} />
          </Box>
        );
      } else if (isVideo) {
        return <Box sx={{ mt: 0.5 }}><video src={downloadUrl} controls style={{ maxWidth: '100%', maxHeight: 250, borderRadius: 8, display: 'block' }} /></Box>;
      } else if (isAudio) {
        return <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center' }}><audio src={downloadUrl} controls style={{ height: 40, width: 250 }} /></Box>;
      } else {
        return (
          <a href={downloadUrl} target="_blank" rel="noreferrer" style={{ color: isMine ? '#001d52' : '#1976d2', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 6, padding: '8px', backgroundColor: isMine ? 'rgba(255,255,255,0.4)' : '#f1f5f9', borderRadius: 8 }}>
            <InsertDriveFileIcon fontSize="small" /> 
            <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: 'break-all' }}>{msg.text || 'Download File'}</Typography>
          </a>
        );
      }
    }
    return <Typography variant="body2" sx={{ color: isMine ? '#001d52' : '#202124', wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>{msg.text}</Typography>;
  };

  return (
    <Paper elevation={0} sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', borderRadius: 3, border: '1px solid #e0e0e0', overflow: 'hidden', bgcolor: '#ffffff' }}>
      {activeChat ? (
        <>
          <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: '#ffffff' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {activeChat.type === 'DIRECT' ? (
                <Avatar src={activeChatProfilePic} sx={{ width: 40, height: 40, bgcolor: '#1976d2' }}>
                  {activeChat.name ? activeChat.name.charAt(0).toUpperCase() : '?'}
                </Avatar>
              ) : (
                <Avatar src={activeChatProfilePic} sx={{ width: 40, height: 40, bgcolor: '#5b5fc7', color: '#fff', fontWeight: 'bold' }}>
                  {activeChat.name ? activeChat.name.charAt(0).toUpperCase() : '#'}
                </Avatar>
              )}
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#202124' }}>{activeChat.name || 'Unnamed Chat'}</Typography>
            </Box>
            <Box>
              <IconButton sx={{ color: '#5b5fc7' }} onClick={() => handleCallClick(false)} title="Start Audio Call"><CallIcon /></IconButton>
              <IconButton sx={{ color: '#5b5fc7' }} onClick={() => handleCallClick(true)} title="Start Video Call"><VideocamIcon /></IconButton>
              {activeChat.type !== 'DIRECT' && (
                <IconButton sx={{ color: '#5b5fc7' }} onClick={onOpenSettings} title="Channel Settings"><SettingsIcon /></IconButton>
              )}
            </Box>
          </Box>

          <Box 
            ref={scrollContainerRef} onScroll={handleScroll} onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop}
            sx={{ flexGrow: 1, p: 3, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 1.5, bgcolor: isDragOver ? '#e3f2fd' : '#f3f2f1', transition: 'background-color 0.2s ease', border: isDragOver ? '2px dashed #1976d2' : 'none' }}
          >
            {loadingMessages ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress size={30} /></Box>
            ) : messages.length === 0 ? (
              <Typography variant="body2" sx={{ textAlign: 'center', color: 'text.secondary', mt: 4 }}>This is the start of your chat history.</Typography>
            ) : (
              messages.map((msg, index) => {
                const isMine = msg.sender === safeEmpId;
                const showHeader = index === 0 || messages[index - 1].sender !== msg.sender;
                const senderProfilePic = `${baseUrl}/employees/${msg.sender}/profile-picture?t=${Date.now()}`;

                return (
                  <Box key={msg.id || index} sx={{ display: 'flex', flexDirection: 'column', alignItems: isMine ? 'flex-end' : 'flex-start', mt: showHeader ? 1 : 0 }}>
                    {!isMine && showHeader && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, ml: 1 }}>
                        <Avatar src={senderProfilePic} sx={{ width: 24, height: 24, bgcolor: '#9c27b0', fontSize: '0.7rem' }}>
                          {msg.sender_name ? msg.sender_name.charAt(0) : '?'}
                        </Avatar>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>{msg.sender_name || msg.sender}</Typography>
                      </Box>
                    )}
                    <Box sx={{ maxWidth: '70%', p: 1.5, px: 2, ml: !isMine ? 4 : 0, borderRadius: 2, bgcolor: isMine ? '#d8e2ff' : '#ffffff', boxShadow: '0 1px 2px rgba(0,0,0,0.05)', borderBottomRightRadius: isMine ? 2 : 12, borderBottomLeftRadius: isMine ? 12 : 2 }}>
                      {renderMessageContent(msg, isMine)}
                      <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: isMine ? 'rgba(0, 29, 82, 0.6)' : 'text.secondary', textAlign: 'right', fontSize: '0.65rem' }}>{msg.time}</Typography>
                    </Box>
                  </Box>
                );
              })
            )}
            <div ref={messagesEndRef} />
          </Box>

          <Box sx={{ p: 2, borderTop: '1px solid #e0e0e0', bgcolor: '#ffffff' }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1, bgcolor: '#f5f5f5', p: 1, borderRadius: 2, border: '1px solid #e0e0e0' }}>
              <input type="file" ref={imageVideoRef} hidden accept="image/*,video/*" onChange={handleFileUpload} />
              <input type="file" ref={audioRef} hidden accept="audio/*" onChange={handleFileUpload} />
              <input type="file" ref={docRef} hidden accept=".pdf,.doc,.docx,.xls,.xlsx,.txt" onChange={handleFileUpload} />
              <input type="file" ref={otherRef} hidden onChange={handleFileUpload} />

              <IconButton size="small" sx={{ color: 'text.secondary', mb: 0.5 }} onClick={(e) => setAnchorElAttach(e.currentTarget)} disabled={isUploading || isRecording}>
                <AttachFileIcon />
              </IconButton>
              <Menu anchorEl={anchorElAttach} open={Boolean(anchorElAttach)} onClose={() => setAnchorElAttach(null)}>
                <MenuItem onClick={() => imageVideoRef.current?.click()}><ImageIcon sx={{ mr: 1, color: '#1976d2' }}/> Photos & Videos</MenuItem>
                <MenuItem onClick={() => audioRef.current?.click()}><HeadphonesIcon sx={{ mr: 1, color: '#ed6c02' }}/> Audio</MenuItem>
                <MenuItem onClick={() => docRef.current?.click()}><InsertDriveFileIcon sx={{ mr: 1, color: '#9c27b0' }}/> Document</MenuItem>
                <MenuItem onClick={() => otherRef.current?.click()}><AttachFileIcon sx={{ mr: 1, color: '#555' }}/> Other File</MenuItem>
              </Menu>

              <IconButton size="small" sx={{ color: 'text.secondary', mb: 0.5 }} onClick={(e) => setAnchorElEmoji(e.currentTarget)} disabled={isUploading || isRecording}>
                <InsertEmoticonIcon />
              </IconButton>
              <Popover open={Boolean(anchorElEmoji)} anchorEl={anchorElEmoji} onClose={() => setAnchorElEmoji(null)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }} transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Grid container spacing={1} sx={{ p: 2, width: 220 }}>
                  {COMMON_EMOJIS.map(emoji => (
                    <Grid item xs={3} key={emoji}>
                      <Typography sx={{ cursor: 'pointer', fontSize: '1.5rem', textAlign: 'center', '&:hover': { bgcolor: '#f1f5f9', borderRadius: 1 } }} onClick={() => { setMessageInput(prev => prev + emoji); setAnchorElEmoji(null); }}>
                        {emoji}
                      </Typography>
                    </Grid>
                  ))}
                </Grid>
              </Popover>

              {isRecording ? (
                <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', gap: 2, px: 2, py: 1.5 }}>
                  <Typography variant="body2" sx={{ color: '#d93025', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1, flexGrow: 1 }}>
                    <span style={{ height: 10, width: 10, borderRadius: '50%', backgroundColor: '#d93025', display: 'inline-block', animation: 'blink 1s infinite' }} />
                    Recording... {formatTime(recTime)}
                  </Typography>
                  <style>{`@keyframes blink { 0% { opacity: 1; } 50% { opacity: 0; } 100% { opacity: 1; } }`}</style>
                  <IconButton color="error" size="small" onClick={() => stopRecording(true)} title="Cancel"><DeleteIcon /></IconButton>
                  <IconButton color="primary" size="small" onClick={() => stopRecording(false)} title="Send Voice Note" sx={{ bgcolor: '#d8e2ff' }}><SendIcon /></IconButton>
                </Box>
              ) : (
                <TextField 
                  fullWidth variant="standard" placeholder={`Type a new message...`} 
                  InputProps={{ disableUnderline: true }}
                  value={messageInput} onChange={(e) => setMessageInput(e.target.value)} onKeyDown={handleKeyPress}
                  sx={{ px: 1, py: 1 }} multiline maxRows={5} disabled={isUploading}
                />
              )}

              {!isRecording && (
                isUploading ? (
                  <Box sx={{ mb: 1, mr: 1 }}><CircularProgress size={24} /></Box>
                ) : messageInput.trim() ? (
                  <IconButton sx={{ color: '#5b5fc7', mb: 0.5 }} onClick={() => onSendMessage(messageInput, 'TEXT', null)}><SendIcon /></IconButton>
                ) : (
                  <IconButton sx={{ color: '#5b5fc7', mb: 0.5 }} onClick={startRecording} title="Send Voice Message"><MicIcon /></IconButton>
                )
              )}
            </Box>
          </Box>
        </>
      ) : (
        <Box sx={{ display: 'flex', height: '100%', alignItems: 'center', justifyContent: 'center', bgcolor: '#f3f2f1' }}>
          <Typography variant="h6" color="text.secondary">Select a chat to start messaging</Typography>
        </Box>
      )}

      <Dialog open={!!previewImage} onClose={() => setPreviewImage(null)} maxWidth="lg" fullWidth>
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: '#202124', color: '#fff' }}>
          <Typography variant="h6">Image Viewer</Typography>
          <Box>
            <Button variant="contained" component="a" href={previewImage} download target="_blank" sx={{ mr: 2, bgcolor: '#5b5fc7', '&:hover': { bgcolor: '#4a4e9e' } }}>
              Download
            </Button>
            <IconButton onClick={() => setPreviewImage(null)} sx={{ color: '#fff' }}><CloseIcon /></IconButton>
          </Box>
        </Box>
        <Box sx={{ bgcolor: '#202124', p: 2, textAlign: 'center', minHeight: '50vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <img src={previewImage} alt="Preview" style={{ maxWidth: '100%', maxHeight: '80vh' }} />
        </Box>
      </Dialog>
    </Paper>
  );
};

export default ChatArena;